// src/components/OrderHistory.js
import React, { useState, useEffect } from 'react';
import {
  Pane,
  Heading,
  Paragraph,
  Spinner,
  Alert,
  Table,
  Badge,
} from 'evergreen-ui';
import { jwtDecode } from 'jwt-decode';
import './OrderHistory.css';

const OrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        let userId;
        try {
          const decodedToken = jwtDecode(token);
          userId = decodedToken.id;
          if (!userId) throw new Error('User ID not found in token');
        } catch (err) {
          throw new Error('Invalid token: ' + err.message);
        }

        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/orders/my-orders?userId=${userId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch orders');
        }

        const data = await response.json();
        setOrders(data);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to fetch orders');
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  if (loading) {
    return (
      <Pane display="flex" justifyContent="center" padding={16}>
        <Spinner />
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane padding={16}>
        <Alert intent="danger" title="Error">
          {error}
        </Alert>
      </Pane>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'order_received':
        return 'blue';
      case 'processing':
        return 'orange';
      case 'shipped':
        return 'purple';
      case 'delivered':
        return 'green';
      default:
        return 'neutral';
    }
  };

  return (
    <Pane maxWidth={1000} margin="auto" padding={16}>
      <Heading size={700} marginBottom={16}>
        Your Orders
      </Heading>

      {orders.length === 0 ? (
        <Paragraph>No orders found.</Paragraph>
      ) : (
        <Table>
          <Table.Head>
            <Table.TextHeaderCell flexBasis={80} flexShrink={0} flexGrow={0}>
              Order ID
            </Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={120} flexShrink={0} flexGrow={0}>
              Customer
            </Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>
              Phone
            </Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>
              Total (KES)
            </Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={100} flexShrink={0} flexGrow={0}>
              Status
            </Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={120} flexShrink={0} flexGrow={0}>
              Delivery Status
            </Table.TextHeaderCell>
            <Table.TextHeaderCell flexBasis={120} flexShrink={0} flexGrow={0}>
              Pickup Point
            </Table.TextHeaderCell>
            <Table.TextHeaderCell flexGrow={1}>
              Items
            </Table.TextHeaderCell>
          </Table.Head>
          <Table.Body>
            {orders.map((order) => (
              <Table.Row key={order.id}>
                <Table.TextCell flexBasis={80} flexShrink={0} flexGrow={0}>
                  #{order.id}
                </Table.TextCell>
                <Table.TextCell flexBasis={120} flexShrink={0} flexGrow={0} isTruncated>
                  {order.customerName}
                </Table.TextCell>
                <Table.TextCell flexBasis={100} flexShrink={0} flexGrow={0}>
                  {order.customerPhone}
                </Table.TextCell>
                <Table.TextCell flexBasis={100} flexShrink={0} flexGrow={0}>
                  {order.totalAmount.toFixed(2)}
                </Table.TextCell>
                <Table.TextCell flexBasis={100} flexShrink={0} flexGrow={0}>
                  <Badge color="blue">{order.status.toUpperCase()}</Badge>
                </Table.TextCell>
                <Table.TextCell flexBasis={120} flexShrink={0} flexGrow={0}>
                  <Badge color={getStatusColor(order.deliveryStatus)}>
                    {order.deliveryStatus.replace('_', ' ').toUpperCase()}
                  </Badge>
                </Table.TextCell>
                <Table.TextCell flexBasis={120} flexShrink={0} flexGrow={0} isTruncated>
                  {order.Location?.pickupPoint || 'N/A'}
                </Table.TextCell>
                <Table.TextCell flexGrow={1}>
                  {order.OrderItems?.length > 0 ? (
                    order.OrderItems.map((item) => (
                      <Paragraph
                        key={item.productId}
                        marginY={4}
                        whiteSpace="normal"
                        overflow="hidden"
                        textOverflow="ellipsis"
                      >
                        {item.Product?.product_title || 'Unknown Product'} - Qty: {item.quantity} - KES {item.unitPrice.toFixed(2)}
                      </Paragraph>
                    ))
                  ) : (
                    <Paragraph marginY={4}>No items found.</Paragraph>
                  )}
                </Table.TextCell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </Pane>
  );
};

export default OrderHistory;